import React, { memo, useCallback, useMemo } from 'react'
import { TextInput } from '../../TextInput/index.js'
import { TextAreaInput } from '../../TextAreaInput/index.js'
import { ColorInput } from '../../ColorInput/index.js'
import { CodeInput } from '../../CodeInput/index.js'
import { RichTextEditor } from '../../RichTextEditor/index.js'
import { FileInput } from '../../FileInput/index.js'
import { capitalize, splitByUppercase } from '../../../utils/text.js'

const computeLabel = (title: any, name: any) => {
  const base = title ?? name
  return typeof base === 'string' ? capitalize(splitByUppercase(base)) : base
}

export const StringType = memo(
  ({ value, onChange, schemaProp, name, disabled }: any) => {
    const { title, description, readOnly, max, format, mime } = schemaProp

    const label = useMemo(() => computeLabel(title, name), [title, name])

    const isDisabled = !!(readOnly || disabled)

    const handleChange = useCallback(
      (v: any) => {
        onChange?.(v, name)
      },
      [onChange, name],
    )

    const commonProps = useMemo(
      () => ({
        value: value ?? '',
        onChange: handleChange,
        label,
        description,
        disabled: isDisabled,
      }),
      [value, handleChange, label, description, isDisabled],
    )

    const leadIcon = useMemo(() => {
      if (format === 'creditCard' || format === 'IBAN' || format === 'BIC')
        return 'card'
      if (format === 'currency') return 'coins'
      if (format === 'EAN') return 'id'
      return null
    }, [format])

    const prefix = useMemo(() => {
      if (format === 'IBAN') return 'IBAN'
      if (format === 'BIC') return 'BIC'
      return null
    }, [format])

    const codeLanguage = useMemo(() => {
      if (format === 'code') return 'bash'
      if (format === 'javascript') return 'js'
      if (format === 'typescript') return 'ts'
      return format
    }, [format])

    const colorFormat = useMemo(() => {
      if (format === 'hexColor') return 'HEX'
      if (format === 'HSL') return 'HSL'
      return 'RGB'
    }, [format])

    const handleFileChange = useCallback(
      (_val: any, _p: any, str: any) => {
        onChange?.(str, name)
      },
      [onChange, name],
    )

    if (format === 'multiline') {
      return <TextAreaInput key={name} {...commonProps} maxLength={max} />
    }

    if (format === 'email') {
      return (
        <TextInput
          key={name}
          {...commonProps}
          type="email"
          maxLength={max}
          placeholder="Add email address"
          errorMessage="Enter a valid email address."
        />
      )
    }

    if (format === 'password') {
      return (
        <TextInput
          key={name}
          {...commonProps}
          type="password"
          passwordToggle
          maxLength={max}
          placeholder="Add password"
        />
      )
    }

    if (format === 'html') {
      return (
        <RichTextEditor
          key={name}
          {...commonProps}
          placeholder="Add rich-text"
        />
      )
    }

    if (format === 'rgbColor' || format === 'HSL' || format === 'hexColor') {
      return (
        <ColorInput
          key={name}
          {...commonProps}
          placeholder="Pick a color"
          format={colorFormat}
        />
      )
    }

    if (
      format === 'typescript' ||
      format === 'rust' ||
      format === 'python' ||
      format === 'markdown' ||
      format === 'json' ||
      format === 'javascript' ||
      format === 'code' ||
      format === 'css'
    ) {
      return (
        <CodeInput
          key={name}
          {...commonProps}
          placeholder="Add code"
          language={codeLanguage}
        />
      )
    }

    if (format === 'URL') {
      return (
        <TextInput
          key={name}
          {...commonProps}
          maxLength={max}
          leadIcon={leadIcon}
          isUrl
          placeholder="www.example.com"
        />
      )
    }

    if (format === 'mobilePhone' || format === 'mobilePhoneLocales') {
      return (
        <TextInput
          key={name}
          {...commonProps}
          maxLength={max}
          placeholder="Add phone number"
          isPhone
        />
      )
    }

    if (mime) {
      return (
        <FileInput
          key={name}
          accept={mime}
          label={title ?? name}
          value={value as any}
          onChange={handleFileChange}
          multiple={false}
          description={description}
          disabled={isDisabled}
          stream="file-upload"
        />
      )
    }

    return (
      <TextInput
        key={name}
        {...commonProps}
        maxLength={max}
        leadIcon={leadIcon}
        prefix={prefix}
      />
    )
  },
  (prev, next) =>
    prev.value === next.value &&
    prev.disabled === next.disabled &&
    prev.name === next.name &&
    prev.onChange === next.onChange &&
    prev.schemaProp === next.schemaProp,
)
